import React, { useState } from 'react'
import Image, { ImageProps } from 'next/image'
import { SanityImage } from '@/types'
import {
  getOptimizedImageUrl,
  getPlaceholderImageUrl,
  getResponsiveImageSrcSet,
  getImageAlt,
  hasValidImage,
} from '@/lib/imageUtils'
import { optimizeImage } from '@/lib/performanceOptimizer'
import { cn } from '@/lib/utils'

interface OptimizedImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  image: SanityImage | null | undefined
  fallbackText?: string
  alt?: string
  width?: number
  height?: number
  sizes?: string
  quality?: number
  priority?: boolean
  className?: string
  fill?: boolean
  showPlaceholder?: boolean
  onLoad?: () => void
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  image,
  fallbackText = 'No image available',
  alt,
  width,
  height,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  quality = 80,
  priority = false,
  className = '',
  fill = false,
  showPlaceholder = true,
  onLoad,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)

  // Handle loading complete
  const handleLoadingComplete = () => {
    setIsLoading(false)
    if (onLoad) onLoad()
  }

  // Handle error
  const handleError = () => {
    setError(true)
    setIsLoading(false)
  }

  // If no valid image, show fallback
  if (!hasValidImage(image)) {
    return (
      <div
        className={cn(
          'bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400',
          fill ? 'w-full h-full' : '',
          className
        )}
        style={!fill && width && height ? { width, height } : {}}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span>{fallbackText}</span>
      </div>
    )
  }

  // If error occurred during loading, show fallback
  if (error) {
    return (
      <div
        className={cn(
          'bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400',
          fill ? 'w-full h-full' : '',
          className
        )}
        style={!fill && width && height ? { width, height } : {}}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>Failed to load image</span>
      </div>
    )
  }

  // Generate image URLs
  const imageUrl = image
    ? getOptimizedImageUrl(image, { width: width || 1200, height, quality })
    : ''
  const placeholderUrl = showPlaceholder && image ? getPlaceholderImageUrl(image) : undefined
  const srcSet = image ? getResponsiveImageSrcSet(image, [400, 800, 1200, 1600], { quality }) : ''
  const imageAlt = alt || (image ? getImageAlt(image) : '')

  // Apply performance optimizations
  const optimizedProps = optimizeImage(imageUrl, {
    width: width || 1200,
    height,
    quality,
    priority,
  })

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={!fill && width && height ? { width, height } : {}}
    >
      <Image
        src={optimizedProps.src}
        alt={imageAlt}
        width={!fill ? width : undefined}
        height={!fill ? height : undefined}
        sizes={optimizedProps.sizes || sizes}
        priority={priority}
        fill={fill}
        loading={optimizedProps.loading}
        className={cn('transition-opacity duration-500', isLoading ? 'opacity-0' : 'opacity-100')}
        onLoadingComplete={handleLoadingComplete}
        onError={handleError}
        placeholder={placeholderUrl ? 'blur' : undefined}
        blurDataURL={placeholderUrl}
        {...(optimizedProps.fetchPriority && { fetchPriority: optimizedProps.fetchPriority })}
        {...props}
      />

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse">
          <svg
            className="w-8 h-8 text-gray-400 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      )}
    </div>
  )
}

export default OptimizedImage
